// The access to the company's mail (23-mail.md, "The access to the mail (v1)"): got at sign-in with
// the same password through the mail server's login API (04, "The password check"), kept per
// account, renewed with the refresh token. No Mail screens use it yet; the admin service, two-step
// sign-in and the company emoji do (27). The password itself is never kept.
import { readJson, writeJson } from './storage';

export interface MailAccess {
  /** The mail server's full address. */
  base: string;
  email: string;
  accessToken: string;
  refreshToken: string;
  /** When the access token expires (ms since 1970). */
  expires: number;
}

const CLIENT = 'desyr';
const SCOPE = 'openid offline_access urn:ietf:params:oauth:scope:mail urn:ietf:params:oauth:scope:calendars urn:ietf:params:oauth:scope:contacts';
const KEY = 'desyr_mail';
const PENDING = 'desyr_mail_pending';

export const base64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
export const random = (n: number) => base64url(crypto.getRandomValues(new Uint8Array(n)));
/** A URL-safe SHA-256 of `text` (a PKCE challenge, a reminder's gateway ID). */
export const sha256url = async (text: string) => base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))));
const returnAddress = () => `${location.origin}/_desyr/login/done`;

/** The mail server's login API (04, "The password check (protocol facts)"). */
export type PasswordAnswer = { type: 'authenticated'; code: string; iss?: string } | { type: 'mfaRequired' } | { type: 'failure' };

export async function passwordCheck(url: string, fields: Record<string, string | undefined>): Promise<PasswordAnswer> {
  const answer = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ type: 'authCode', ...fields }),
  });
  const body = answer.status === 200 ? await answer.json().catch(() => undefined) : undefined;
  if (body?.type === 'authenticated' && typeof body.client_code === 'string') return { type: 'authenticated', code: body.client_code, iss: body.iss };
  if (body?.type === 'mfaRequired' || body?.type === 'failure') return { type: body.type };
  throw Object.assign(new Error('password check'), { status: answer.status });
}

async function tokenRequest(mail: string, fields: Record<string, string>) {
  const answer = await fetch(`${mail.replace(/\/+$/, '')}/auth/token`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: CLIENT, ...fields }),
  });
  const body = await answer.json().catch(() => ({}));
  if (!answer.ok || !body.access_token) throw Object.assign(new Error('mail token'), { status: answer.status, code: body.error });
  return body as { access_token: string; refresh_token?: string; expires_in?: number };
}

/**
 * Gets the mail access with the work address and password (and the two-step code): the code from
 * the login API for desyr's own client with PKCE, exchanged at the token endpoint.
 */
export async function connectMail(passwordUrl: string, mail: string, email: string, password: string, mfaToken?: string): Promise<MailAccess | 'code' | undefined> {
  const verifier = random(32);
  const challenge = await sha256url(verifier);
  const answer = await passwordCheck(passwordUrl, {
    accountName: email,
    accountSecret: password,
    clientId: CLIENT,
    redirectUri: returnAddress(),
    scope: SCOPE,
    state: random(12),
    nonce: random(12),
    codeChallenge: challenge,
    codeChallengeMethod: 'S256',
    mfaToken,
  });
  if (answer.type === 'mfaRequired') return 'code';
  if (answer.type !== 'authenticated') return undefined;
  const t = await tokenRequest(mail, { grant_type: 'authorization_code', code: answer.code, redirect_uri: returnAddress(), code_verifier: verifier });
  if (!t.refresh_token) return undefined;
  return { base: mail, email, accessToken: t.access_token, refreshToken: t.refresh_token, expires: Date.now() + (t.expires_in ?? 3600) * 1000 };
}

/** Until the chat sign-in has finished, the access waits in this tab (at most 10 minutes). */
export function keepPending(access: MailAccess): void {
  try {
    sessionStorage.setItem(PENDING, JSON.stringify({ access, at: Date.now() }));
  } catch {
    // storage refused: Mail asks once later
  }
}

/** The chat sign-in finished: the waiting access is kept for that account. */
export function adoptPending(userId: string): void {
  try {
    const raw = sessionStorage.getItem(PENDING);
    sessionStorage.removeItem(PENDING);
    const pending = raw && (JSON.parse(raw) as { access: MailAccess; at: number });
    if (pending && Date.now() - pending.at < 10 * 60_000) keep(userId, pending.access);
  } catch {
    // nothing waiting
  }
}

const all = () => readJson<Record<string, MailAccess>>(KEY) ?? {};

export function keep(userId: string, access: MailAccess | undefined): void {
  const next = { ...all() };
  if (access) next[userId] = access;
  else delete next[userId];
  writeJson(KEY, Object.keys(next).length ? next : undefined);
}

export const mailAccess = (userId: string): MailAccess | undefined => all()[userId];

/** Mail needs the password again: the access is forgotten (23, "Renewal"). */
export class MailSignedOut extends Error {
  readonly kind = 'MS';
}

/**
 * A usable access token: renewed when under a minute is left (or `refused` after a 401). A refresh
 * the mail server no longer takes forgets the access.
 */
export async function mailToken(userId: string, refused = false): Promise<string> {
  const access = mailAccess(userId);
  if (!access) throw new MailSignedOut();
  if (!refused && access.expires - Date.now() > 60_000) return access.accessToken;
  try {
    const t = await tokenRequest(access.base, { grant_type: 'refresh_token', refresh_token: access.refreshToken });
    const renewed = { ...access, accessToken: t.access_token, refreshToken: t.refresh_token ?? access.refreshToken, expires: Date.now() + (t.expires_in ?? 3600) * 1000 };
    keep(userId, renewed);
    return renewed.accessToken;
  } catch (e) {
    if ((e as { code?: string }).code === 'invalid_grant') {
      keep(userId, undefined);
      throw new MailSignedOut();
    }
    throw Object.assign(new Error('mail token'), { status: 503 });
  }
}
